export const INVESTOR_FIELDS = ["name", "email", "phone", "firm", "type", "notes"] as const;
export type InvestorField = (typeof INVESTOR_FIELDS)[number];
export type ImportRow = Record<InvestorField, string>;

export type RowStatus = "new" | "existing" | "already_member" | "review" | "blocked" | "duplicate";
export type Resolution = "keep_existing" | "create_separate" | "reactivate_membership";

export interface PreviewRow {
  index: number;
  rowNumber: number;
  name: string;
  email: string;
  status: RowStatus;
  investorId?: number;
  existingName?: string;
  issues: string[];
  resolution?: Resolution;
  duplicateOf?: number;
}

export interface PreviewResponse {
  reviewHash: string;
  cohort: { id: number; name: string };
  rows: PreviewRow[];
  summary: Record<string, number>;
  warnings: string[];
}

export interface CommitResponse {
  created: number;
  added: number;
  alreadyMembers: number;
  skipped: number;
}

export const TEMPLATE_CSV = `name,email,phone,firm,type,notes
Rachit Sharma,rachit@example.com,+91 98xxxxxx01,Sharma Family Office,family_office,Met at Q2 demo day
Priya Mehta,priya@example.com,+91 99xxxxxx02,Mehta Capital,vc,
Anand Singh,anand@example.com,,,angel,Referred by an existing cohort member
`;

/** Minimal CSV parser supporting quoted fields, embedded commas and newlines. */
export function parseCSV(text: string): { headers: string[]; rows: Record<string, string>[] } {
  const lines: string[][] = [];
  let cur: string[] = [];
  let field = "";
  let inQuote = false;
  const src = text.replace(/^\uFEFF/, "");
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuote) {
      if (c === '"' && src[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') inQuote = false;
      else field += c;
    } else if (c === '"') inQuote = true;
    else if (c === ",") { cur.push(field); field = ""; }
    else if (c === "\n") { cur.push(field); lines.push(cur); cur = []; field = ""; }
    else if (c !== "\r") field += c;
  }
  if (field.length > 0 || cur.length > 0) { cur.push(field); lines.push(cur); }
  if (lines.length === 0) return { headers: [], rows: [] };
  const headers = lines[0].map(h => h.trim());
  const rows = lines.slice(1).filter(r => r.some(c => c.trim() !== "")).map(r => {
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => { obj[h] = (r[i] ?? "").trim(); });
    return obj;
  });
  return { headers, rows };
}

/** Keep only investor fields (case-insensitive header match); report ignored headers. */
export function normalizeRows(headers: string[], rows: Record<string, string>[]): { rows: ImportRow[]; ignored: string[]; missing: InvestorField[] } {
  const map = new Map<string, InvestorField>();
  const ignored: string[] = [];
  for (const h of headers) {
    const key = h.trim().toLowerCase() as InvestorField;
    if ((INVESTOR_FIELDS as readonly string[]).includes(key) && !Array.from(map.values()).includes(key)) map.set(h, key);
    else if (h.trim()) ignored.push(h.trim());
  }
  const present = new Set(map.values());
  const missing = (["name", "email"] as InvestorField[]).filter(f => !present.has(f));
  const out = rows.map(r => {
    const row = emptyRow();
    map.forEach((field, header) => { row[field] = (r[header] ?? "").trim(); });
    return row;
  });
  return { rows: out, ignored, missing };
}

export function emptyRow(): ImportRow {
  return { name: "", email: "", phone: "", firm: "", type: "", notes: "" };
}

export const RESOLUTION_OPTIONS: Record<Resolution, string> = {
  keep_existing: "Keep existing profile, add to cohort",
  create_separate: "Create a separate investor",
  reactivate_membership: "Reactivate cohort membership",
};

export function isReady(s: RowStatus) {
  return s === "new" || s === "existing" || s === "already_member";
}

/** Rows selected by default after preview: only ready rows. */
export function defaultSelection(rows: PreviewRow[]): Set<number> {
  return new Set(rows.filter(r => isReady(r.status)).map(r => r.index));
}

/** Whether a row may be included given chosen resolutions. */
export function canSelect(row: PreviewRow, resolutions: Record<string, string>): boolean {
  if (isReady(row.status)) return true;
  if (row.status === "review") return !!resolutions[String(row.index)];
  return false;
}

/** Filter selection to rows that are legal; used just before commit. */
export function effectiveSelection(rows: PreviewRow[], selected: Set<number>, resolutions: Record<string, string>): number[] {
  return rows.filter(r => selected.has(r.index) && canSelect(r, resolutions)).map(r => r.index).sort((a, b) => a - b);
}

/** Resolutions only for selected review rows. */
export function resolutionsFor(rows: PreviewRow[], selected: number[], resolutions: Record<string, string>): Record<string, string> {
  const sel = new Set(selected);
  const out: Record<string, string> = {};
  for (const r of rows) {
    const k = String(r.index);
    if (r.status === "review" && sel.has(r.index) && resolutions[k]) out[k] = resolutions[k];
  }
  return out;
}

export function rowsToCSV(rows: ImportRow[]): string {
  const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  return [INVESTOR_FIELDS.join(","), ...rows.map(r => INVESTOR_FIELDS.map(f => esc(r[f])).join(","))].join("\n") + "\n";
}
